import { useCallback, useState, type VFC } from 'react';
import { useHistory } from 'react-router-dom';
import { Table } from 'components';
import { useDeleteMeterModule } from 'api/hooks/meter-modules/useDeleteModule';
import type { SorterResult, TablePaginationConfig } from 'antd/es/table/interface';
import { RoleName, type SerializedMeterAddon } from '@enmon/commons/lib/tools';
import { useUserInfo } from '@/hooks/useUser';
import type { Key, SortOrder } from 'antd/lib/table/interface';
import { getModulesColumnsConfig } from '../utils/getColumnsConfig';
import { useModulesListFilterWithPagination } from '../hooks/useModulesListFilterWithPagination';

interface ModulesTableProps {
  isFetchingAddons: boolean;
  addonsData: SerializedMeterAddon[];
}

export const ModulesTable: VFC<ModulesTableProps> = props => {
  const { isFetchingAddons, addonsData } = props;

  // State
  const history = useHistory();
  const { role } = useUserInfo() ?? {};
  const { pagination, setFilterAndPaginationState } = useModulesListFilterWithPagination();
  const isMagicRole = role?.name === RoleName.Magic;
  const [sortState, setSortState] = useState<{ field: Key | null; order: SortOrder }>({
    field: null,
    order: null,
  });

  // Mutations
  const { mutate: deleteModule, isLoading: isDeletingModule } = useDeleteMeterModule();

  // Handlers
  const handleEditClick = useCallback(
    (moduleId: SerializedMeterAddon['id']) => {
      history.push(`${history.location.pathname}/${moduleId}`);
    },
    [history],
  );

  const handleDelete = useCallback(
    (moduleId: SerializedMeterAddon['id']) => {
      deleteModule(moduleId);
    },
    [deleteModule],
  );

  const handleTableChange = (
    newPagination: TablePaginationConfig,
    _: any,
    sorter: SorterResult<SerializedMeterAddon> | SorterResult<SerializedMeterAddon>[],
  ) => {
    const { columnKey, order } = (Array.isArray(sorter) ? sorter[0] : sorter) ?? {};
    const newFilter = {
      ...(columnKey &&
        order && {
          sort: { [columnKey.toString()]: `${order.slice(0, -3)}` },
        }),
    };
    setSortState({ field: columnKey, order });
    setFilterAndPaginationState(newFilter, newPagination);
  };

  // Table Config
  const isLoading = isFetchingAddons || isDeletingModule;

  const tableColumns = getModulesColumnsConfig({
    loading: isLoading,
    isMagicRole,
    onEdit: handleEditClick,
    onDelete: handleDelete,
    sortState,
  });

  return (
    <Table<SerializedMeterAddon>
      columns={tableColumns}
      dataSource={addonsData}
      loading={isLoading}
      pagination={{
        ...pagination,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} modules`,
      }}
      rowKey={item => item.id}
      scroll={{ x: 'fit-content' }}
      onChange={handleTableChange}
    />
  );
};
